import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

export const ERROR_TYPES = {
  VALIDATION: 'validation',
  NETWORK: 'network',
  PERMISSION: 'permission',
  SERVER: 'server',
  NOT_FOUND: 'not_found',
  UNKNOWN: 'unknown'
};

const ErrorContext = createContext(null);

export const ErrorProvider = ({ children }) => {
  const [errors, setErrors] = useState([]);
  
  // --- FUNCIÓN CORE BLINDADA ---
  // Envolvemos el setErrors en setTimeout aquí mismo.
  // Así, cualquier componente que llame a addError() estará protegido automáticamente.
  const addError = useCallback((message, type = ERROR_TYPES.UNKNOWN, context = null, technical = null) => {
    const errorId = uuidv4();
    
    // EL TRUCO MAESTRO: Deferir la actualización al siguiente ciclo del event loop
    setTimeout(() => {
        setErrors(prev => {
            // Evitar duplicados exactos muy rápidos si es necesario (opcional)
            const isDuplicate = prev.some(e => e.message === message && e.context === context && (new Date() - e.timestamp < 500));
            if (isDuplicate) return prev;

            return [...prev, {
              id: errorId,
              message,
              type,
              context,
              technical,
              timestamp: new Date()
            }];
        });
    }, 0);
    
    if (process.env.NODE_ENV === 'production' && technical) {
      console.error('Error técnico:', technical);
    }
    
    return errorId;
  }, []);
  
  const removeError = useCallback((errorId) => {
    setErrors(prev => prev.filter(err => err.id !== errorId));
  }, []);
  
  const clearErrors = useCallback(() => {
    // También protegemos el limpiado
    setTimeout(() => setErrors([]), 0);
  }, []);
  
  const addValidationError = useCallback((message, context) => 
    addError(message, ERROR_TYPES.VALIDATION, context), [addError]);
  
  const addNetworkError = useCallback((message = 'Problema de conexión. Verifica tu internet.', context) => 
    addError(message, ERROR_TYPES.NETWORK, context), [addError]);
  
  const addPermissionError = useCallback((message = 'No tienes permiso para realizar esta acción.', context) => 
    addError(message, ERROR_TYPES.PERMISSION, context), [addError]);
  
  const handleFirebaseError = useCallback((error, context) => {
    let message = 'Ha ocurrido un error inesperado.';
    let type = ERROR_TYPES.UNKNOWN;
    
    if (error.code) {
      const errorMappings = {
        'permission-denied': { message: 'No tienes permiso para realizar esta acción.', type: ERROR_TYPES.PERMISSION },
        'not-found': { message: 'No se encontró el recurso solicitado.', type: ERROR_TYPES.NOT_FOUND },
        'network-request-failed': { message: 'Problema de conexión. Verifica tu internet.', type: ERROR_TYPES.NETWORK },
        'auth/user-not-found': { message: 'El usuario no existe.', type: ERROR_TYPES.VALIDATION },
        'auth/wrong-password': { message: 'Contraseña incorrecta.', type: ERROR_TYPES.VALIDATION },
        'auth/email-already-in-use': { message: 'Este email ya está en uso.', type: ERROR_TYPES.VALIDATION },
        'auth/weak-password': { message: 'La contraseña es demasiado débil.', type: ERROR_TYPES.VALIDATION },
      };
      
      const mapping = errorMappings[error.code];
      if (mapping) {
        message = mapping.message;
        type = mapping.type;
      }
    }
    
    // addError ya tiene el setTimeout interno, así que aquí lo llamamos directo
    return addError(message, type, context, error);
  }, [addError]);
  
  // Captura de errores globales (Tu sugerencia implementada)
  useEffect(() => {
    const handleGlobalError = (event) => {
      // No prevenimos default para que se vea en consola del navegador también si se desea, 
      // pero si quieres silenciarlo visualmente usa preventDefault()
      // event.preventDefault(); 
      const { message, filename, lineno, colno, error } = event;
      
      // addError ya es asíncrono (seguro), así que podemos llamarlo directo
      addError(
        'Ha ocurrido un error inesperado en la aplicación', 
        ERROR_TYPES.UNKNOWN, 
        'global', 
        { message, filename, lineno, colno, stack: error?.stack }
      );
    };
    
    const handleUnhandledRejection = (event) => {
      // event.preventDefault();
      addError(
        'Ha ocurrido un error inesperado (promesa no manejada)', 
        ERROR_TYPES.UNKNOWN, 
        'global-promise', 
        { reason: event.reason, stack: event.reason?.stack }
      );
    };
    
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [addError]);
  
  const value = {
    errors,
    addError,
    removeError,
    clearErrors,
    addValidationError,
    addNetworkError,
    addPermissionError,
    handleFirebaseError
  };
  
  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError debe ser usado dentro de un ErrorProvider');
  }
  return context;
};

export const ErrorMessage = ({ error, onClose }) => {
  if (!error) return null;
  
  const getErrorStyles = () => {
    switch (error.type) {
      case ERROR_TYPES.NETWORK:
        return { bg: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-700', icon: 'text-orange-400' };
      case ERROR_TYPES.PERMISSION:
        return { bg: 'bg-yellow-50', border: 'border-yellow-400', text: 'text-yellow-700', icon: 'text-yellow-400' };
      case ERROR_TYPES.VALIDATION:
        return { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', icon: 'text-blue-400' };
      default:
        return { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-700', icon: 'text-red-400' };
    }
  };
  
  const styles = getErrorStyles();
  
  return (
    <div className={`${styles.bg} border-l-4 ${styles.border} p-4 relative`} role="alert">
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertCircle className={`h-5 w-5 ${styles.icon}`} />
        </div>
        <div className="ml-3 pr-8">
          <p className={`text-sm ${styles.text}`}>{error.message}</p>
        </div>
        {onClose && (
          <button 
            onClick={onClose} 
            className={`absolute top-4 right-4 ${styles.text} hover:opacity-75`}
            aria-label="Cerrar mensaje de error"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export const ErrorDisplay = () => {
  const { errors, removeError } = useError();
  if (!errors.length) return null;
  return (
    <div className="fixed top-4 right-4 z-50 w-96 space-y-2" aria-live="polite">
      {errors.map(error => (
        <ErrorMessage 
          key={error.id} 
          error={error} 
          onClose={() => removeError(error.id)} 
        />
      ))}
    </div>
  );
};

export { ErrorContext };